import TokenMigrator from '@/utils/tokenMigrator';

/**
 * 登录态校验服务
 */
class AuthService {
  /**
   * 从cookie中获取token
   * @param {string} name cookie名称
   * @returns {string|null} token值
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * 设置cookie
   * @param {string} name cookie名称
   * @param {string} value cookie值
   * @param {number} days 过期天数
   */
  setCookie(name, value, days = 7) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  /**
   * 删除cookie
   * @param {string} name cookie名称
   */
  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  /**
   * 尝试从cookie迁移token到localStorage
   * @returns {string|null} 迁移的token
   */
  migrateTokenFromCookie() {
    return TokenMigrator.migrateToLocalStorage('katoken', false);
  }

  /**
   * 获取token
   * @returns {string|null} token值
   */
  getToken() {
    // 开发环境下返回模拟token
    if (process.env.NODE_ENV === 'development') {
      const devToken = localStorage.getItem('dev_katoken')
      if (devToken) {
        return devToken
      }
      // 如果没有设置开发token，返回一个默认的测试token
      const defaultDevToken = 'dev_test_token_123456789'
      localStorage.setItem('dev_katoken', defaultDevToken)
      return defaultDevToken
    }
    
    return localStorage.getItem('katoken')
  }

  /**
   * 设置token
   * @param {string} token token值
   */
  setToken(token) {
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('dev_katoken', token)
    } else {
      localStorage.setItem('katoken', token)
    }
  }

  /**
   * 清除token
   */
  clearToken() {
    if (process.env.NODE_ENV === 'development') {
      localStorage.removeItem('dev_katoken')
    } else {
      localStorage.removeItem('katoken')
    }
    // 同时清除可能的cookie token
    const possibleTokenNames = ['katoken', 'token', 'access_token', 'auth_token', 'session_token', 'sso_token'];
    possibleTokenNames.forEach(name => this.deleteCookie(name));
    // 清除角色检查标记
    sessionStorage.removeItem('role_checked');
  }

  /**
   * 设置开发环境token（仅开发环境使用）
   * @param {string} token 开发token
   */
  setDevToken(token) {
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('dev_katoken', token)
    }
  }

  /**
   * 生成开发环境token（仅开发环境使用）
   * @returns {string} 生成的token
   */
  generateDevToken() {
    if (process.env.NODE_ENV === 'development') {
      const timestamp = Date.now()
      const randomStr = Math.random().toString(36).substring(2, 15)
      const newToken = `dev_token_${timestamp}_${randomStr}`
      localStorage.setItem('dev_katoken', newToken)
      return newToken
    }
    return null
  }

  /**
   * 检查用户是否已登录
   * @returns {Promise<boolean>} 是否已登录
   */
  async checkLoginStatus() {
    try {
      // 开发环境跳过登录检查
      if (process.env.NODE_ENV === 'development') {
        console.log('开发环境：跳过登录检查')
        return true
      }

      // 获取token（会自动尝试从cookie迁移）
      const token = this.getToken()
      if (!token) {
        return false
      }

      // 如果环境变量未定义，使用默认值
      const kssoUrl = process.env.VUE_APP_KSSO_URL || 'https://gateway.keco.vip'
      const response = await fetch(`${kssoUrl}/release/ksso/isLogin`, {
        method: 'GET',
        headers: {
          'katoken': token,
          'Accept': '*/*',
          'Connection': 'keep-alive'
        }
      });

      if (!response.ok) {
        return false
      }

      const result = await response.json()
      return result.code === 200 && result.data === true
    } catch (error) {
      console.error('登录态校验失败:', error)
      // 开发环境返回true，生产环境返回false
      return process.env.NODE_ENV === 'development'
    }
  }

  /**
   * 跳转到登录页
   * @param {string} redirectUrl 重定向地址
   */
  redirectToLogin(redirectUrl = window.location.href) {
    // 开发环境跳过登录跳转
    if (process.env.NODE_ENV === 'development') {
      console.log('开发环境：跳过登录跳转')
      return
    }

    // 如果环境变量未定义，使用默认值
    const ssoUrl = process.env.VUE_APP_SSO_URL || 'http://sso.withflows.com'
    const loginUrl = `${ssoUrl}?redirect=${encodeURIComponent(redirectUrl)}`
    window.location.href = loginUrl
  }

  /**
   * 初始化token（在应用启动时调用）
   */
  initializeToken() {
    // 尝试从cookie迁移token
    const migratedToken = this.migrateTokenFromCookie();
    if (migratedToken) {
      console.log('成功从cookie迁移token到localStorage');
    }
  }

  /**
   * 获取API请求的公共请求头
   * @returns {Object} 请求头对象
   */
  getApiHeaders() {
    const token = this.getToken();
    const headers = {
      'Accept': '*/*',
      'Connection': 'keep-alive',
      'appid': '100108',
      'channel': '100703'
    };
    
    if (token) {
      headers['katoken'] = token;
    }
    
    return headers;
  }

  /**
   * 检查是否启用开发环境角色测试
   * @returns {boolean} 是否启用
   */
  isDevRoleTestEnabled() {
    if (process.env.NODE_ENV !== 'development') {
      return false
    }
    return localStorage.getItem('dev_role_test_enabled') === 'true'
  }

  /**
   * 设置开发环境角色测试开关
   * @param {boolean} enabled 是否启用
   */
  setDevRoleTestEnabled(enabled) {
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('dev_role_test_enabled', enabled ? 'true' : 'false')
    }
  }

  /**
   * 检查用户是否有系统后台内的角色
   * @returns {Promise<boolean>} 是否有角色
   */
  async checkRole() {
    try {
      // 开发环境：如果未启用角色测试，返回true
      if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
        console.log('开发环境：跳过角色检查（未启用角色测试）')
        return true
      }

      const token = this.getToken()
      if (!token) {
        // 开发环境且未启用角色测试时返回true
        if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
          return true
        }
        return false
      }

      const baseUrl = process.env.VUE_APP_KSSO_URL || 'http://gateway.keco.vip'
      const response = await fetch(`${baseUrl}/release/kauth/checkRole`, {
        method: 'GET',
        headers: this.getApiHeaders()
      });

      if (!response.ok) {
        // 开发环境且未启用角色测试时返回true
        if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
          return true
        }
        return false
      }

      const result = await response.json()
      return result.code === 200 && result.data === true
    } catch (error) {
      console.error('角色检查失败:', error)
      // 开发环境且未启用角色测试时返回true
      if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
        return true
      }
      return false
    }
  }

  /**
   * 获取用户的角色列表
   * @returns {Promise<Array<string>>} 角色列表
   */
  async getRole() {
    try {
      // 开发环境：如果未启用角色测试，返回默认角色列表
      if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
        console.log('开发环境：返回默认角色列表（未启用角色测试）')
        return ['Recuriting', 'Creator', 'Operation', 'VideoProducer']
      }

      const token = this.getToken()
      if (!token) {
        // 开发环境且未启用角色测试时返回默认列表
        if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
          return ['Recuriting', 'Creator', 'Operation', 'VideoProducer']
        }
        return []
      }

      const baseUrl = process.env.VUE_APP_KSSO_URL || 'http://gateway.keco.vip'
      const response = await fetch(`${baseUrl}/release/kauth/getRole`, {
        method: 'GET',
        headers: this.getApiHeaders()
      });

      if (!response.ok) {
        // 开发环境且未启用角色测试时返回默认列表
        if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
          return ['Recuriting', 'Creator', 'Operation', 'VideoProducer']
        }
        return []
      }

      const result = await response.json()
      if (result.code === 200 && Array.isArray(result.data)) {
        return result.data
      }
      
      return []
    } catch (error) {
      console.error('获取角色列表失败:', error)
      // 开发环境且未启用角色测试时返回默认列表
      if (process.env.NODE_ENV === 'development' && !this.isDevRoleTestEnabled()) {
        return ['Recuriting', 'Creator', 'Operation', 'VideoProducer']
      }
      return []
    }
  }
}

export default new AuthService(); 